import { getMockCatalog } from '@/modules/search/utils/mock-catalog'
import type { QuoteDraftItem } from '@/modules/products/types'
import { purchaseOptimizer } from '../engines'
import {
  optimizationConfigRepository,
  optimizationHistoryRepository,
} from '../repositories'
import type {
  OptimizationHistoryEntry,
  OptimizationResult,
  OptimizationStrategy,
  OptimizationWeights,
  OptimizerDemandItem,
  PlanComparison,
  SupplierOfferCandidate,
} from '../types'

/**
 * PurchaseOptimizerService — quote demand → offers → optimize → history/export.
 */
export class PurchaseOptimizerService {
  demandsFromDraft(items: QuoteDraftItem[]): OptimizerDemandItem[] {
    return items.map((item, index) => ({
      id: `demand-${item.productId}-${index}`,
      productId: item.productId,
      description: stripSupplierSuffix(item.descricao),
      sku: item.codigoInterno,
      quantity: item.quantity,
      currentUnitPrice: item.valorManual,
    }))
  }

  /** Demo basket when the quote draft is empty. */
  sampleDemands(count = 5): OptimizerDemandItem[] {
    const catalog = getMockCatalog()
    const step = Math.floor(catalog.length / count)
    return Array.from({ length: count }, (_, i) => {
      const product = catalog[i * step]!
      return {
        id: `sample-${product.id}`,
        productId: product.id,
        description: product.description,
        sku: product.sku,
        quantity: 1 + (i % 3),
        currentUnitPrice: round2(product.pixPrice * 1.08),
        brand: product.brand,
        model: product.model,
        ean: product.ean,
      }
    })
  }

  buildOffers(demands: OptimizerDemandItem[]): SupplierOfferCandidate[] {
    const catalog = getMockCatalog()
    const offers: SupplierOfferCandidate[] = []

    for (const demand of demands) {
      const matched = findMatches(catalog, demand)
      const usedSuppliers = new Set<string>()

      for (const product of matched) {
        if (usedSuppliers.has(product.supplier)) continue
        usedSuppliers.add(product.supplier)
        offers.push({
          offerId: `${demand.id}:${product.id}`,
          productKey: product.ean || product.sku,
          demandId: demand.id,
          supplier: product.supplier,
          description: product.description,
          sku: product.sku,
          unitPrice: product.pixPrice,
          stock: product.stock,
          deliveryDays: product.estimatedDelivery,
          brand: product.brand,
          model: product.model,
        })
      }

      // Ensure multi-supplier coverage for optimization demos
      if (usedSuppliers.size < 4) {
        const seed = matched[0] ?? catalog[hash(demand.description) % catalog.length]!
        const suppliers = [...new Set(catalog.map((p) => p.supplier))]
        for (const supplier of suppliers) {
          if (usedSuppliers.has(supplier)) continue
          const variance = ((hash(`${demand.id}:${supplier}`) % 17) - 8) / 100
          const stock = 5 + (hash(supplier + demand.id) % 40)
          offers.push({
            offerId: `${demand.id}:synth:${supplier}`,
            productKey: seed.ean || demand.sku,
            demandId: demand.id,
            supplier,
            description: demand.description,
            sku: demand.sku || seed.sku,
            unitPrice: round2(Math.max(10, (demand.currentUnitPrice || seed.pixPrice) * (1 + variance))),
            stock,
            deliveryDays: 1 + (hash(supplier) % 12),
            brand: seed.brand,
            model: seed.model,
          })
          usedSuppliers.add(supplier)
          if (usedSuppliers.size >= 6) break
        }
      }
    }

    return offers
  }

  optimize(
    demands: OptimizerDemandItem[],
    strategy: OptimizationStrategy,
  ): { result: OptimizationResult; comparison: PlanComparison; offersCount: number } {
    const weights = optimizationConfigRepository.getWeights()
    purchaseOptimizer.setWeights(weights)

    const offers = this.buildOffers(demands)
    const { result, comparison } = purchaseOptimizer.optimize({
      demands,
      offers,
      strategy,
    })

    return { result, comparison, offersCount: offers.length }
  }

  getWeights(): OptimizationWeights {
    return optimizationConfigRepository.getWeights()
  }

  saveWeights(weights: OptimizationWeights): OptimizationWeights {
    const normalized = purchaseOptimizer.setWeights(weights)
    return optimizationConfigRepository.saveWeights(normalized)
  }

  listHistory(): OptimizationHistoryEntry[] {
    return optimizationHistoryRepository.list()
  }

  recordHistory(input: {
    quoteId: string
    strategy: OptimizationStrategy
    economy: number
    totalCost: number
    supplierCount: number
  }): OptimizationHistoryEntry {
    return optimizationHistoryRepository.add(input)
  }

  exportCsv(result: OptimizationResult): string {
    const header = ['Produto', 'Fornecedor', 'SKU', 'Qtd', 'Preço', 'Subtotal', 'Prazo']
    const rows = result.items.map((item) =>
      [
        csv(item.product),
        csv(item.supplier),
        csv(item.sku),
        String(item.quantity),
        String(item.unitPrice),
        String(item.subtotal),
        String(item.delivery),
      ].join(','),
    )
    rows.unshift(header.join(','))
    rows.push('')
    rows.push(`Total produtos,${result.productCost}`)
    rows.push(`Frete,${result.freightCost}`)
    rows.push(`Total,${result.totalCost}`)
    rows.push(`Economia,${result.economy}`)
    return rows.join('\n')
  }

  exportPdfMeta(result: OptimizationResult) {
    return {
      ok: true as const,
      message: 'PDF preparado para motor futuro',
      title: 'Otimização de Compras',
      totalCost: result.totalCost,
      economy: result.economy,
      supplierCount: result.supplierCount,
      strategy: result.strategy,
    }
  }
}

function findMatches(
  catalog: ReturnType<typeof getMockCatalog>,
  demand: OptimizerDemandItem,
) {
  const sku = demand.sku.trim().toLowerCase()
  const desc = demand.description.toLowerCase()
  const tokens = desc.split(/[^a-z0-9áéíóúãõâêôç]+/i).filter((t) => t.length > 2)

  const bySku = sku ? catalog.filter((p) => p.sku.toLowerCase() === sku) : []
  if (bySku.length) return bySku

  const scored = catalog
    .map((product) => {
      const hay = `${product.description} ${product.brand} ${product.model} ${product.sku}`.toLowerCase()
      let score = 0
      for (const token of tokens) {
        if (hay.includes(token)) score += 1
      }
      if (demand.brand && product.brand.toLowerCase() === demand.brand.toLowerCase()) score += 2
      return { product, score }
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)

  return scored.slice(0, 12).map((row) => row.product)
}

function stripSupplierSuffix(description: string): string {
  return description.replace(/\s·\s[a-z0-9_-]+$/i, '').trim()
}

function hash(value: string): number {
  let h = 0
  for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) >>> 0
  return h
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

function csv(value: string): string {
  if (value.includes(',') || value.includes('"')) {
    return `"${value.replaceAll('"', '""')}"`
  }
  return value
}

export const purchaseOptimizerService = new PurchaseOptimizerService()
